/*
 * Cart drawer + sticky buy bar.
 *
 * Every product form posts to /cart/add as normal HTML. This script only
 * upgrades that: it sends the same FormData to /cart/add.js, re-renders the
 * drawer from /cart.js and opens it. Any failure falls back to the plain form
 * post, so a customer can always buy.
 *
 * The drawer is a native <dialog> (focus trap, Escape, inert page for free).
 * Checkout is a plain POST to /cart with name=checkout: Shopify's own handoff.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  var root = document.documentElement;
  var money = function (cents) { return window.vxMoney ? window.vxMoney(cents, drawer.dataset.moneyFormat, drawer.dataset.moneySample) : (cents / 100).toFixed(2); };


  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function announce(count) {
    document.dispatchEvent(new CustomEvent('vitelyn:cart:updated', { detail: { itemCount: count } }));
  }

  /* ---------------- drawer rendering ---------------- */

  var protectId = drawer ? +drawer.dataset.protectVariant || 0 : 0;
  var protectBox = drawer && drawer.querySelector('[data-protect]');
  var protectToggle = drawer && drawer.querySelector('[data-protect-toggle]');

  /* ---------------- bag upgrade (1 -> 2 bottles) ---------------- */

  var coreId = drawer ? +drawer.dataset.coreVariant || 0 : 0;
  var upsellPct = drawer ? parseInt(drawer.dataset.upsellPct, 10) || 0 : 0;
  var upsell = drawer && drawer.querySelector('[data-upsell]');
  var tierPcts = drawer && drawer.dataset.tierPcts ? drawer.dataset.tierPcts.split(',').map(Number) : [];

  function session(key, value) {
    try {
      if (value === undefined) return sessionStorage.getItem(key);
      sessionStorage.setItem(key, value);
    } catch (e) { return null; }
  }

  function renderUpsell(goods) {
    if (!upsell) return;
    var line = goods.length === 1 && goods[0].variant_id === coreId && goods[0].quantity === 1 && !goods[0].selling_plan_allocation ? goods[0] : null;
    var show = !!(line && upsellPct > 0 && session('vx-upsell-no') !== '1');
    upsell.hidden = !show;
    if (!show) return;
    var total = 2 * (line.price - Math.floor(line.price * upsellPct / 100));
    upsell.dataset.key = line.key;
    upsell.querySelector('[data-upsell-body]').textContent = drawer.dataset.labelUpsell
      .replace('%T', money(total)).replace('%S', money(line.price * 2 - total));
    if (session('vx-upsell-seen') !== '1') {
      session('vx-upsell-seen', '1');
      window.vxTrack && window.vxTrack('upsell_viewed', { offer: 'second_bottle', discount_pct: upsellPct });
    }
  }

  // The tier prices on the product page are theme settings; the discount that
  // honours them lives in the admin. If a multi-bottle line comes back without
  // it, report it rather than let the two drift apart unnoticed.
  function checkTiers(goods) {
    goods.forEach(function (i) {
      if (i.variant_id !== coreId || i.selling_plan_allocation || i.quantity < 2) return;
      var p = tierPcts[Math.min(i.quantity, 3) - 2] || 0;
      if (!p) return;
      // Same per-unit rounding Shopify uses, so any difference is real.
      var expected = i.quantity * (i.price - Math.floor(i.price * p / 100));
      if (i.final_line_price > expected) {
        if (window.console) console.warn('Vitelyn: bundle discount missing', { quantity: i.quantity, expected: expected, charged: i.final_line_price });
        window.vxTrack && window.vxTrack('offer_price_mismatch', { quantity: i.quantity, expected: expected, charged: i.final_line_price });
      }
    });
  }

  if (upsell) {
    upsell.querySelector('[data-upsell-add]').addEventListener('click', function (e) {
      var btn = e.currentTarget;
      btn.disabled = true;
      status('');
      changeLine(upsell.dataset.key, 2)
        .then(function (cart) {
          window.vxTrack && window.vxTrack('upsell_accepted', { offer: 'second_bottle', discount_pct: upsellPct });
          render(cart);
        })
        .catch(function () { status(drawer.dataset.labelError); })
        .then(function () { btn.disabled = false; });
    });
    upsell.querySelector('[data-upsell-no]').addEventListener('click', function () {
      session('vx-upsell-no', '1');
      upsell.hidden = true;
      window.vxTrack && window.vxTrack('upsell_dismissed', { offer: 'second_bottle' });
    });
  }

  function render(cart) {
    if (!drawer) return;
    var d = drawer.dataset;
    var list = drawer.querySelector('[data-cart-lines]');
    var empty = drawer.querySelector('[data-cart-empty]');
    var checkout = drawer.querySelector('[data-cart-checkout]');
    // The optional protection fee is shown as a checkbox, not as a line.
    var goods = cart.items.filter(function (i) { return i.variant_id !== protectId; });
    var protect = cart.items.filter(function (i) { return i.variant_id === protectId; })[0];
    var count = goods.reduce(function (n, i) { return n + i.quantity; }, 0);
    // Gift cards are emailed, so a bag of only gift cards has nothing to protect.
    var shipped = goods.some(function (i) { return i.requires_shipping; });

    // Protection with nothing to protect is removed, quietly.
    if (protect && !shipped) {
      changeLine(protect.key, 0).then(render);
      return;
    }

    list.innerHTML = goods.map(function (item) {
      var plan = item.selling_plan_allocation ? '<p class="lx-line-item__plan">' + esc(item.selling_plan_allocation.selling_plan.name) + '</p>' : '';
      return '<li class="lx-line-item" data-key="' + esc(item.key) + '">' +
        '<div class="lx-line-item__thumb" aria-hidden="true">' + (item.image ? '<img class="lx-line-item__svg" alt="" loading="lazy" src="' + esc(item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=160') + '">' : '') + '</div>' +
        '<div class="lx-line-item__main">' +
          '<p class="lx-line-item__title">' + esc(item.product_title) + '</p>' + plan +
          '<p class="lx-line-item__each lx-num">' + esc(money(item.final_price)) + esc(d.labelEach) + '</p>' +
          '<div class="lx-line-item__ctl">' +
            '<div class="lx-qty__ctl lx-qty__ctl--sm">' +
              '<button type="button" class="lx-qty__btn" data-change="' + (item.quantity - 1) + '" aria-label="' + esc(d.labelDown) + '"><span aria-hidden="true" class="lx-qty__minus"></span></button>' +
              '<span class="lx-qty__value lx-num" aria-live="polite">' + item.quantity + '</span>' +
              '<button type="button" class="lx-qty__btn" data-change="' + (item.quantity + 1) + '" aria-label="' + esc(d.labelUp) + '"' + (item.quantity >= 6 ? ' disabled' : '') + '><span aria-hidden="true" class="lx-qty__plus"></span></button>' +
            '</div>' +
            '<button type="button" class="lx-line-item__remove" data-change="0">' + esc(d.labelRemove) + '</button>' +
          '</div>' +
        '</div>' +
        '<p class="lx-line-item__price lx-num">' + (item.original_line_price > item.final_line_price ? '<s class="lx-line-item__was">' + esc(money(item.original_line_price)) + '</s>' : '') + esc(money(item.final_line_price)) + '</p>' +
      '</li>';
    }).join('');
    empty.hidden = count > 0;
    checkout.disabled = count === 0;
    if (protectBox) { protectBox.hidden = !shipped; protectToggle.checked = !!protect; protectToggle.disabled = false; }
    // Applied codes (e.g. LOCKIN10 from the scratch card), so the saving is visible.
    var dl = drawer.querySelector('[data-cart-discounts]');
    if (dl) {
      var saved = {};
      goods.forEach(function (i) {
        (i.line_level_discount_allocations || []).forEach(function (a) {
          var t = (a.discount_application && a.discount_application.title) || 'Discount';
          saved[t] = (saved[t] || 0) + a.amount;
        });
      });
      (cart.cart_level_discount_applications || []).forEach(function (a) {
        saved[a.title] = (saved[a.title] || 0) + (a.total_allocated_amount || 0);
      });
      dl.innerHTML = Object.keys(saved).filter(function (t) { return saved[t] > 0; }).map(function (t) {
        return '<li><span>' + esc(t) + '</span><span class="lx-num">\u2212' + esc(money(saved[t])) + '</span></li>';
      }).join('');
    }
    drawer.querySelector('[data-cart-subtotal]').textContent = money(cart.total_price);
    renderUpsell(goods);
    checkTiers(goods);
    announce(count);
  }

  function changeLine(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { if (!r.ok) throw r; return r.json(); });
  }

  if (protectToggle) protectToggle.addEventListener('change', function () {
    var on = protectToggle.checked;
    protectToggle.disabled = true;
    status('');
    var req = on
      ? fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: protectId, quantity: 1 }] }) })
          .then(function (r) { if (!r.ok) throw r; return getCart(); })
      : getCart().then(function (cart) {
          var line = cart.items.filter(function (i) { return i.variant_id === protectId; })[0];
          return line ? changeLine(line.key, 0) : cart;
        });
    req.then(render).catch(function () {
      protectToggle.checked = !on; protectToggle.disabled = false;
      status(drawer.dataset.labelError);
    });
  });

  function status(msg) {
    var el = drawer && drawer.querySelector('[data-cart-status]');
    if (el) el.textContent = msg || '';
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  /* ---------------- open / close ---------------- */

  function open() {
    if (!drawer || drawer.open) return;
    root.classList.add('is-locked');
    drawer.showModal();
    // Next frame, so the closed state is painted before the transition runs.
    requestAnimationFrame(function () { requestAnimationFrame(function () { drawer.classList.add('is-open'); }); });
  }

  function close() {
    if (!drawer || !drawer.open) return;
    drawer.classList.remove('is-open');
    var done = function () { drawer.close(); root.classList.remove('is-locked'); };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return done();
    setTimeout(done, 300);
  }

  if (drawer) {
    drawer.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    // A click on the backdrop lands on the dialog itself.
    drawer.addEventListener('click', function (e) { if (e.target === drawer) close(); });
    drawer.querySelector('[data-cart-close]').addEventListener('click', close);

    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-change]');
      if (!btn) return;
      var line = btn.closest('[data-key]');
      btn.disabled = true;
      status('');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.dataset.key, quantity: Math.max(0, parseInt(btn.dataset.change, 10) || 0) })
      })
        .then(function (r) { if (!r.ok) throw r; return r.json(); })
        .then(render)
        .catch(function () { status(drawer.dataset.labelError); btn.disabled = false; });
    });

    document.querySelectorAll('[data-cart-open]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        open();
        getCart().then(render).catch(function () {});
      });
    });
  }

  /* ---------------- add to cart ---------------- */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!drawer || !form.action || form.action.indexOf('/cart/add') === -1) return;
    e.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    var formStatus = form.querySelector('[data-add-status]');
    if (btn) { btn.disabled = true; btn.classList.add('is-busy'); }
    if (formStatus) formStatus.textContent = '';

    fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) { var err = new Error(body.description || body.message || ''); err.shown = true; throw err; }
          return body;
        });
      })
      .then(getCart)
      .then(function (cart) { render(cart); open(); })
      .catch(function (err) {
        // A known refusal (sold out, quantity limit) is shown where the
        // customer is looking. Anything else: fall back to the real form post.
        if (err && err.shown && formStatus) formStatus.textContent = err.message;
        else form.submit();
      })
      .then(function () { if (btn) { btn.disabled = false; btn.classList.remove('is-busy'); } });
  });

  /* ---------------- sticky buy bar ---------------- */

  var bar = document.querySelector('[data-buybar]');
  if (bar && 'IntersectionObserver' in window) {
    // Hidden while any of these is on screen: the hero's own actions, the full
    // buy section, and the footer. One primary action in view at a time.
    var blockers = document.querySelectorAll('.lx-hero__actions, #buy, .lx-footer');
    var visible = new Set();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) visible.add(en.target); else visible.delete(en.target); });
      var show = visible.size === 0 && window.scrollY > 200;
      bar.classList.toggle('is-shown', show);
      bar.inert = !show;
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    });
    blockers.forEach(function (b) { io.observe(b); });
    // Re-evaluate on first scroll: the observer only fires on changes.
    window.addEventListener('scroll', function once() {
      window.removeEventListener('scroll', once);
      var show = visible.size === 0 && window.scrollY > 200;
      bar.classList.toggle('is-shown', show);
      bar.inert = !show;
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { passive: true });
  }
})();
